/**
 * Nothing in the Studio may wait forever. Every await that can hang goes through withTimeout,
 * so a screen either finishes or says what failed and offers a retry. A spinner is never the end state.
 */
export class TimeoutError extends Error {
  readonly what: string;

  constructor(what: string, ms: number) {
    super(`${what} did not answer within ${ms}ms`);
    this.name = 'TimeoutError';
    this.what = what;
  }
}

/** Rejects with TimeoutError when the work has not settled in time. The work itself is left to finish or not. */
export function withTimeout<T>(work: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new TimeoutError(what, ms)), ms);
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/**
 * What decides that the session bootstrap has to run again.
 *
 * undefined (Supabase has not answered yet) and null (answered: nobody is signed in) must not
 * collapse into the same value. They used to, because both `undefined?.user.id` and
 * `null?.user.id` are undefined, so the effect never re-ran and a signed out visitor was left
 * on the loading spinner and never reached the login screen.
 */
export function sessionKey(session: { user: { id: string } } | null | undefined): string {
  if (session === undefined) return 'unknown';
  if (session === null) return 'signed_out';
  return session.user.id;
}

/**
 * The email we already send links to #crm/<id>, with no slash after the hash. HashRouter expects
 * the path to start with a slash (#/crm/<id>), so without this a click from the email lands nowhere.
 * Returns the corrected hash, or null when the hash is already fine.
 */
export function normalizeHash(hash: string): string | null {
  if (hash === '' || hash === '#') return null;
  const path = hash.startsWith('#') ? hash.slice(1) : hash;
  if (path.startsWith('/')) return null;
  return `#/${path}`;
}

/** Applies normalizeHash to the current location, now and whenever the hash changes. */
export function keepHashRoutable(location: { hash: string }, apply: (hash: string) => void): void {
  const fixed = normalizeHash(location.hash);
  if (fixed !== null) apply(fixed);
}
